import { useState, useRef, useLayoutEffect, type ReactNode } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Home, Calendar, Clock, LogOut, Loader2, UserRoundCog, Users, UserPlus, BarChart3, MessageSquare, UserCircle, HelpCircle, MoreHorizontal } from "lucide-react";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useDemo } from "@/features/demo/hooks/useDemo";

interface SubItem {
  path: string;
  label: string;
  icon: ReactNode;
}

interface SidebarItem {
  id: string;
  path?: string;
  label: string;
  icon: ReactNode;
  type: "link" | "submenu" | "action";
  subItems?: SubItem[];
}

const effectivenessSubItems: SubItem[] = [
  { path: "/employees", label: "Pracownicy", icon: <UserPlus className="w-4 h-4" /> },
  { path: "/timesheet", label: "Czas pracy", icon: <Clock className="w-4 h-4" /> },
  { path: "/reports", label: "Raporty", icon: <BarChart3 className="w-4 h-4" /> },
  { path: "/substitutions", label: "Zastępstwa", icon: <UserRoundCog className="w-4 h-4" /> },
];

const bottomItems: SidebarItem[] = [
  { id: "profile", path: "/profile", label: "Profil", icon: <UserCircle className="w-6 h-6" />, type: "link" },
  { id: "logout", label: "Wyloguj", icon: <LogOut className="w-6 h-6" />, type: "action" },
];

export function Sidebar() {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const asideRef = useRef<HTMLElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { logout } = useAuth();
  const { isDemoMode } = useDemo();

  const mainItems: SidebarItem[] = [
    { id: "home", path: "/dashboard", label: "Home", icon: <Home className="w-6 h-6" />, type: "link" },
    { id: "schedules", path: "/schedules", label: "Grafiki", icon: <Calendar className="w-6 h-6" />, type: "link" },
    { id: "team", label: "Zespół", icon: <Users className="w-6 h-6" />, type: "submenu", subItems: effectivenessSubItems },
    { id: "chat", path: "/chat", label: "Czat", icon: <MessageSquare className="w-6 h-6" />, type: "link" },
    { id: "help", path: isDemoMode ? "/demo-help" : "/help", label: "Pomoc", icon: <HelpCircle className="w-6 h-6" />, type: "link" },
  ];

  const allItems: SidebarItem[] = [...mainItems, ...bottomItems];
  const [maxVisible, setMaxVisible] = useState(allItems.length);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    await logout();
    navigate("/login");
  };

  // Measure available space and determine how many items fit
  useLayoutEffect(() => {
    const aside = asideRef.current;
    const nav = navRef.current;
    if (!aside || !nav) return;

    const calculate = () => {
      const navTop = nav.getBoundingClientRect().top;
      const asideBottom = aside.getBoundingClientRect().bottom;
      const availableHeight = asideBottom - navTop;

      // Measure actual item height from first rendered item
      const ul = nav.querySelector("ul");
      if (!ul || ul.children.length === 0) return;
      const firstItem = ul.children[0] as HTMLElement;
      const itemHeight = firstItem.getBoundingClientRect().height;

      // space-y-2 = 8px gap between items
      const gap = 8;
      const effectiveItemHeight = itemHeight + gap;

      // How many items fit (+gap correction: last item has no trailing gap)
      const fits = Math.floor((availableHeight + gap) / effectiveItemHeight);

      if (fits >= allItems.length) {
        setMaxVisible(allItems.length);
      } else {
        // -1 to reserve a slot for "Więcej"
        setMaxVisible(Math.max(1, fits - 1));
      }
    };

    calculate();
    const observer = new ResizeObserver(calculate);
    observer.observe(aside);
    return () => observer.disconnect();
  }, [allItems.length]);

  const needsOverflow = maxVisible < allItems.length;
  const visibleItems = needsOverflow ? allItems.slice(0, maxVisible) : allItems;
  const overflowItems = needsOverflow ? allItems.slice(maxVisible) : [];

  const isItemActive = (item: SidebarItem): boolean => {
    if (item.path) return location.pathname.startsWith(item.path);
    if (item.subItems) return item.subItems.some((sub) => location.pathname.startsWith(sub.path));
    return false;
  };

  const isOverflowActive = overflowItems.some(isItemActive);

  const closeMenus = () => setOpenMenu(null);

  const iconCircleClass = (active: boolean) =>
    `w-12 h-12 rounded-full flex items-center justify-center transition-colors border ${
      active ? "bg-primary/20 text-primary border-border shadow-sm" : "bg-muted text-foreground border-border group-hover:bg-muted/80"
    }`;

  const renderSidebarItem = (item: SidebarItem) => {
    const active = isItemActive(item);

    if (item.type === "link" && item.path) {
      return (
        <li key={item.id}>
          <NavLink
            to={item.path}
            onClick={() => {
              if (active) {
                queryClient.invalidateQueries();
              }
            }}
            className={`flex flex-col items-center justify-center gap-2 py-4 px-2 transition-all relative group ${
              active ? "text-primary" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {active && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-12 bg-primary rounded-r-full" />}
            <div className={iconCircleClass(active)}>{item.icon}</div>
            <span className="text-xs font-medium text-center">{item.label}</span>
          </NavLink>
        </li>
      );
    }

    if (item.type === "submenu" && item.subItems) {
      return (
        <li key={item.id} className="relative">
          <button
            onClick={() => setOpenMenu(openMenu === item.id ? null : item.id)}
            className={`w-full flex flex-col items-center justify-center gap-2 py-4 px-2 transition-all relative group ${
              active ? "text-primary" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {active && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-12 bg-primary rounded-r-full" />}
            <div className={iconCircleClass(active)}>{item.icon}</div>
            <span className="text-xs font-medium text-center">{item.label}</span>
          </button>
          {openMenu === item.id && (
            <>
              <div className="fixed inset-0 z-[59]" onClick={closeMenus} />
              <div className="absolute top-0 left-full ml-2 w-48 bg-card rounded-lg shadow-xl border border-border py-2 z-[70]">
                {item.subItems.map((sub) => (
                  <button
                    key={sub.path}
                    onClick={() => {
                      closeMenus();
                      navigate(sub.path);
                    }}
                    className={`w-full px-4 py-2 text-left hover:bg-muted flex items-center gap-3 transition-colors ${
                      location.pathname.startsWith(sub.path) ? "text-primary bg-primary/10" : "text-foreground"
                    }`}
                  >
                    <span className="text-primary">{sub.icon}</span>
                    <span className="text-sm font-medium flex-1">{sub.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </li>
      );
    }

    if (item.type === "action") {
      return (
        <li key={item.id}>
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="w-full flex flex-col items-center justify-center gap-2 py-4 px-2 transition-all group disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <div className="w-12 h-12 rounded-full flex items-center justify-center transition-colors border bg-muted text-foreground border-border group-hover:bg-destructive/10 group-hover:text-destructive group-hover:border-destructive/30">
              {isLoggingOut ? <Loader2 className="w-6 h-6 animate-spin" /> : item.icon}
            </div>
            <span className="text-xs font-medium text-muted-foreground group-hover:text-destructive">{item.label}</span>
          </button>
        </li>
      );
    }

    return null;
  };

  const renderOverflowMenuItems = () => {
    const menuItems: ReactNode[] = [];

    for (const item of overflowItems) {
      if (item.type === "submenu" && item.subItems) {
        menuItems.push(
          <div key={`${item.id}-header`} className="px-4 pt-3 pb-1">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{item.label}</span>
          </div>
        );
        for (const sub of item.subItems) {
          menuItems.push(
            <button
              key={sub.path}
              onClick={() => {
                closeMenus();
                navigate(sub.path);
              }}
              className={`w-full px-4 py-2 text-left hover:bg-muted flex items-center gap-3 transition-colors ${
                location.pathname.startsWith(sub.path) ? "text-primary bg-primary/10" : "text-foreground"
              }`}
            >
              <span className="text-primary">{sub.icon}</span>
              <span className="text-sm font-medium flex-1">{sub.label}</span>
            </button>
          );
        }
      } else if (item.type === "action") {
        menuItems.push(
          <button
            key={item.id}
            onClick={() => {
              closeMenus();
              handleLogout();
            }}
            disabled={isLoggingOut}
            className="w-full px-4 py-2 text-left hover:bg-destructive/10 flex items-center gap-3 transition-colors text-foreground hover:text-destructive disabled:opacity-60"
          >
            <span className="text-muted-foreground">
              {isLoggingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
            </span>
            <span className="text-sm font-medium flex-1">{item.label}</span>
          </button>
        );
      } else if (item.path) {
        menuItems.push(
          <button
            key={item.id}
            onClick={() => {
              closeMenus();
              navigate(item.path!);
            }}
            className={`w-full px-4 py-2 text-left hover:bg-muted flex items-center gap-3 transition-colors ${
              location.pathname.startsWith(item.path) ? "text-primary bg-primary/10" : "text-foreground"
            }`}
          >
            <span className="text-primary">{item.icon}</span>
            <span className="text-sm font-medium flex-1">{item.label}</span>
          </button>
        );
      }
    }

    return menuItems;
  };

  return (
    <aside
      ref={asideRef}
      className="fixed left-0 top-0 h-screen w-20 bg-card text-foreground hidden md:flex flex-col items-center border-r border-border shadow-sm z-[60]"
    >
      {/* Logo */}
      <div className="pt-4 pb-2 w-full flex justify-center shrink-0">
        <img
          src="/assets/logo.png"
          alt="BEE in TIME Logo"
          className="w-[100px] h-[100px] object-contain"
        />
      </div>

      {/* Navigation */}
      <nav ref={navRef} className="flex-1 w-full flex flex-col">
        {needsOverflow ? (
          /* Overflow mode: single list with "Więcej" button */
          <ul className="space-y-2">
            {visibleItems.map(renderSidebarItem)}

            {/* "Więcej" overflow button */}
            <li className="relative">
              <button
                onClick={() => setOpenMenu(openMenu === "more" ? null : "more")}
                className={`w-full flex flex-col items-center justify-center gap-2 py-4 px-2 transition-all relative group ${
                  isOverflowActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {isOverflowActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-12 bg-primary rounded-r-full" />
                )}
                <div className={iconCircleClass(isOverflowActive)}>
                  <MoreHorizontal className="w-6 h-6" />
                </div>
                <span className="text-xs font-medium text-center">Więcej</span>
              </button>

              {openMenu === "more" && (
                <>
                  <div className="fixed inset-0 z-[59]" onClick={closeMenus} />
                  <div className="absolute bottom-0 left-full ml-2 w-48 bg-card rounded-lg shadow-xl border border-border py-2 z-[70]">
                    {renderOverflowMenuItems()}
                  </div>
                </>
              )}
            </li>
          </ul>
        ) : (
          /* Full mode: main items top, profile/logout bottom with separator */
          <>
            <ul className="space-y-2">
              {mainItems.map(renderSidebarItem)}
            </ul>
            <div className="mt-auto w-full">
              <div className="mx-4 border-t border-border" />
              <ul className="space-y-2 mt-2">
                {bottomItems.map(renderSidebarItem)}
              </ul>
            </div>
          </>
        )}
      </nav>
    </aside>
  );
}
